// @ts-check
import React, { useMemo, memo, useCallback } from 'react';
import RGL, { WidthProvider } from 'react-grid-layout';
import classnames from 'classnames';
import './Dashboard.scss';
import _ from 'lodash';
import Panel from './Panel';
import { DashboardPropType } from './DashboardPropTypes';
import PropTypes from 'prop-types';
import { useResizeDetector } from 'react-resize-detector';
import useBoolean from '../../utils/hooks/useBoolean';
import { Button, ButtonVariant, Stack, StackItem, Tooltip } from '@patternfly/react-core';
import { Icons } from '../Common';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import { useTranslation } from '../../localization/useTranslation';
import panelStyle from './Panel.module.scss';
import { throttleDate } from 'services/datetime';
import usePanelsVisibility from './usePanelsVisibility';

const ReactGridLayout = WidthProvider(RGL);

/**
 * @constant
 * @type {[number, number]}
 */
const CONTAINER_PADDING = [0, 0];

export const DashboardSizeSettings = {
  rowHeight: 30,
  margin: 10,
  columnCount: 24,
  panelMinWidth: 4,
  panelMinHeight: 4,
  compactWidth: 800
};

//
//
/**
 * This makes sure that panels are sorted by their position in the grid and not by creation date (index in array)
 * It will make the layout better for mobile devices (panels above other panels will still be above the other panels)
 * @param {Panel[]} panels
 * @param {number} columnCount
 * @returns {Panel[]}
 */
const sortPanels = (panels, columnCount) => {
  return panels?.sort((panelA, panelB) => {
    const offsetA = panelA.grid.y * columnCount + panelA.grid.x;
    const offsetB = panelB.grid.y * columnCount + panelB.grid.x;
    return offsetA - offsetB;
  });
};

/**
 * @typedef {object} DashboardProps
 * @property {import('./DashboardPropTypes').DashboardType} dashboard
 * @property {boolean} [isResizable]
 * @property {boolean} [isDraggable]
 * @property {(layout: RGL.Layout[]) => void} [onLayoutChange]
 * @property {(panel: Panel) => void} [onEditPanel]
 * @property {number} [rowHeight]
 * @property {number} [margin]
 * @property {number} [columnCount]
 * @property {number} [panelMinWidth]
 * @property {number} [panelMinHeight]
 * @property {number} [compactWidth]
 * @property {string} [selectedPanelId]
 * @property {(panel: Panel) => void} [onClickPanel]
 * @property {() => Date} dashboardDate
 */

/**
 *
 * @param {DashboardProps} props
 * @returns {JSX.Element}
 */
const Dashboard = ({
  dashboard,

  isResizable = false,
  isDraggable = false,

  onLayoutChange,
  onEditPanel,

  // From size-me

  rowHeight = DashboardSizeSettings.rowHeight,
  margin = DashboardSizeSettings.margin,
  columnCount = DashboardSizeSettings.columnCount,
  panelMinWidth = DashboardSizeSettings.panelMinWidth,
  panelMinHeight = DashboardSizeSettings.panelMinHeight,
  compactWidth = DashboardSizeSettings.compactWidth,

  selectedPanelId,
  onClickPanel,
  dashboardDate: externalDashboardDate
}) => {
  const size = useResizeDetector();
  const T = useTranslation();
  const isCompact = size.width < compactWidth;

  const onResizeStop = useCallback(() => {
    _.defer(() => {
      window.dispatchEvent(new Event('resize'));
    });
  }, []);

  // This will make sure all data sources can get the same request time (if needed)
  // and thus increase the chance of hitting the same network cache
  const dashboardDate = useMemo(() => {
    return externalDashboardDate ?? throttleDate(1000);
  }, [externalDashboardDate]);

  const panelVisibility = usePanelsVisibility(dashboard?.data?.panels);

  const panelComponents = useMemo(() => {
    const sortedPanels = sortPanels(dashboard?.data?.panels, columnCount);
    return _.compact(_.map(sortedPanels, panel => {
      const isVisible = panelVisibility[panel.id];
      if (!onEditPanel && !isVisible) {
        return null;
      }
      const dataGrid = {
        x: panel.grid.x ?? 0,
        y: panel.grid.y ?? 0,
        w: Math.max(panel.grid.w ?? 0, panelMinWidth),
        h: Math.max(panel.grid.h ?? 0, panelMinHeight),
        minW: panelMinWidth,
        minH: panelMinHeight
      };
      const onClickTitle = () => {
        onClickPanel?.(panel);
      };
      return (
        <div
          key={panel.id}
          data-grid={dataGrid}
          className={classnames({
            'panel-full-width': isCompact,
            'panel-selected': selectedPanelId === panel.id
          })}
        >
          <Panel panel={panel} isVisible={isVisible} onEditPanel={onEditPanel} onClickTitle={onClickTitle} dashboardDate={dashboardDate}/>
        </div>
      );
    }));
  }, [dashboard?.data?.panels, columnCount, panelVisibility, panelMinWidth, panelMinHeight, isCompact, selectedPanelId, onEditPanel, dashboardDate, onClickPanel]);

  /**
   * @type {[number, number]}
   */
  const _margin = useMemo(() => ([margin, margin]), [margin]);

  const [hasFullScreen, , , toggleFullscreen] = useBoolean(false);
  // TODO: Enable fullscreen button here once we are happy with layout and scaling
  const useFullScreen = false;
  const dashboardContent = (
    <Stack className={classNames('dashboard-container', { 'dashboard-fullscreen': hasFullScreen })}>
      { useFullScreen && <StackItem className={'dashboard-toolbar'}>
        <Tooltip
          content={T.common.togglefullscreen}
          /* Using left only here because in fullscreen the tooltip might be shown below button and causing scrollbar
          *  to be visible
          */
          flipBehavior={['left']}>
          <Button
            className={'dashboard-button'}
            variant={ButtonVariant.plain}
            onClick={toggleFullscreen}
            icon={hasFullScreen ? <Icons.ExitFullscreen/> : <Icons.Fullscreen/>}/>
        </Tooltip>
      </StackItem>
      }
      <StackItem isFilled className={'dashboard-content'}>
        <ReactGridLayout
          innerRef={size.ref}
          className={'grid-layout'}
          containerPadding={CONTAINER_PADDING}
          cols={columnCount}
          onResizeStop={onResizeStop}
          margin={_margin}
          rowHeight={rowHeight}
          onLayoutChange={onLayoutChange}
          isResizable={isResizable}
          isDraggable={isDraggable}
          draggableHandle={'.' + panelStyle.titleContent}
        >
          { panelComponents }
        </ReactGridLayout>
      </StackItem>
    </Stack>
  );

  return hasFullScreen ? ReactDOM.createPortal(dashboardContent, document.body) : dashboardContent;

};

Dashboard.propTypes = {
  dashboard: DashboardPropType,
  isResizable: PropTypes.bool,
  isDraggable: PropTypes.bool,
  onLayoutChange: PropTypes.func,
  onEditPanel: PropTypes.func,

  rowHeight: PropTypes.number,
  margin: PropTypes.number,
  columnCount: PropTypes.number,
  panelMinWidth: PropTypes.number,
  panelMinHeight: PropTypes.number,
  compactWidth: PropTypes.number,

  selectedPanelId: PropTypes.string,
  onClickPanel: PropTypes.func
};

export default memo(Dashboard);
